import { Typography, InputLabel, Alert } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import HolzfinderDateInput from "components/inputs/HolzfinderDateInput";
import HolzfinderCommaNumberInput from "components/inputs/HolzfinderCommaNumberInput";
import HolzfinderSelect from "components/inputs/HolzfinderSelect";
import { selectLotOffer, selectLotOfferErrors, selectLotOfferGrossPrice, setPriceGross, setPriceNet, setPublishedAt, setStatus } from "features/lotOffer/lotOfferSlice";
import { LotOfferInterface } from "interfaces/LotOfferInterface";
import LotOfferGrid from "./LotOfferGrid";
import { useEffect, useState } from "react";
import { formatNumberInput, validateNumberInput } from "functions/lotPrice";
import { LotOfferStatus } from "interfaces/LotOfferStatus";

const LotOfferOptionsInputs = {

  price_type: {
    label: "Angezeigeter Angebotspreis ist",
    default: "Netto",
    options: [
      {
        value: "gross",
        label: "Brutto",
      },
      {
        value: "net",
        label: "Netto",
      },
    ],
  },
  price_gross: {
    label: "Brutto-Preis €/Fm",
  },
  price_per_solid_cubic_meter: {
    label: "Netto-Preis €/Fm",
  },
  activation: {
    label: "Aktivierung",
    default: LotOfferStatus.INACTIVE,
    options: [
      {
        value: LotOfferStatus.ACTIVE,
        label: "Aktiv",
      },
      {
        value: LotOfferStatus.INACTIVE,
        label: "Inaktiv",
      },
      {
        value: LotOfferStatus.PLANNED,
        label: "Aktivierung planen",
      },
    ],
  },
};

export default function LotOfferOptions() {
  const lotOfferData = useAppSelector(selectLotOffer) as LotOfferInterface;
  const grossPrice = useAppSelector(selectLotOfferGrossPrice);
  const errors = useAppSelector(selectLotOfferErrors);
  const [isZeroPrice, setIsZeroPrice] = useState(grossPrice === "0");
  const dispatch = useAppDispatch();
  const handleActivationChange = (event: any) => {
    // Check if price is not 0
    if (isZeroPrice && event.target.value !== LotOfferStatus.INACTIVE ) {
      return false;
    }
    dispatch(setStatus(event.target.value));
  };
  useEffect(() => {
    setIsZeroPrice(grossPrice === "0,00");
  }, [grossPrice]);
  useEffect(() => {
    if (isZeroPrice) {
      dispatch(setStatus(LotOfferStatus.INACTIVE));
    }
  }, [isZeroPrice, dispatch]);

  const [error, setError] = useState<string | null>(null);
  const handlePriceChangeNet = (value: string) => {
    const isValidPrice = /^\d+((\.|,)\d+)?$/.test(value);
    if (isValidPrice) {
      setError("");
      const netPrice = Number(value.replaceAll(",", "."));
      dispatch(setPriceNet(netPrice.toFixed(3)));
    } else {
      setError("Bitte geben Sie einen positiven Betrag an.");
      dispatch(setPriceNet(""));
    }
  };

  const handlePriceChangeGross = (value: string) => {
    const isValidPrice = /^\d+((\.|,)\d+)?$/.test(value);
    if (isValidPrice) {
      setError("");
      const grossPrice = Number(value.replaceAll(",", "."));
      dispatch(setPriceGross(grossPrice.toFixed(3)));
    } else {
      setError("Bitte geben Sie einen positiven Betrag an.");
      dispatch(setPriceGross(""));
    }
  };


  const handlePlannedDate = (value: string) => {
    dispatch(setPublishedAt(value));
  };

  const lotOfferSold = lotOfferData?.status === "sold";

  return (
    <LotOfferGrid>
      <Typography
        fontWeight={"bold"}
        variant="h5"
        sx={{
          mt: 2,
          mb: 1,
          color: "#072D26",
        }}
      >
        OPTIONEN
      </Typography>

      <InputLabel>{LotOfferOptionsInputs.price_per_solid_cubic_meter.label}</InputLabel>
      {error && <Alert severity="error">{error}</Alert>}
      {/*TODO:  Make it with more numbers after comma */}
      <HolzfinderCommaNumberInput
        unit="Netto Preis €/Fm"
        value={lotOfferData && lotOfferData.price_per_solid_cubic_meter ? 
          formatNumberInput(parseFloat(lotOfferData.price_per_solid_cubic_meter).toFixed(2).replaceAll(".", ",")) : 
          "0,00"}
        onChange={handlePriceChangeNet}
        validateInput={(s) => validateNumberInput(s, 3)}
        formatInput={formatNumberInput}
        disabled={lotOfferSold}
        helperText={errors.price_per_solid_cubic_meter?.reduce((acc, curr) => acc + " " + curr, "")}
        error={!!errors?.price_per_solid_cubic_meter}
      />
      <InputLabel>{LotOfferOptionsInputs.price_gross.label}</InputLabel>
      <HolzfinderCommaNumberInput
        unit="Brutto Preis €/Fm"
        value={lotOfferData ? grossPrice.replaceAll(".", ",") : "0,00"} // price_per_solid_cubic_meter * 1.${ vatRate / 100 } eg: 100*1.055
        onChange={handlePriceChangeGross}
        validateInput={(s) => validateNumberInput(s, 3)}
        formatInput={formatNumberInput}
        disabled={lotOfferSold}
      />
      <HolzfinderSelect
        id={"activation"}
        label={LotOfferOptionsInputs.activation.label}
        options={LotOfferOptionsInputs.activation.options}
        currentValue={lotOfferData?.status || LotOfferOptionsInputs.activation.default}
        defaultValue={LotOfferOptionsInputs.activation.default}
        onChange={handleActivationChange}
        inputProps={{ "aria-label": "Without label" }}
        disabled={lotOfferSold || isZeroPrice}
      />
      {lotOfferData?.status === LotOfferStatus.PLANNED ? <HolzfinderDateInput value={lotOfferData.published_at} onChange={handlePlannedDate} disablePast={true} /> : undefined}
    </LotOfferGrid>
  );
}
